import { Box, Button, Text } from "@dehub/theme";
import { InfoIcon, WarningIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { BusinessFormValues, MarketingState } from "./business-form-model";
import classes from "../business.module.css";

type Props = Readonly<{
  values: BusinessFormValues;
  marketing: MarketingState;
  /** Already confirmed: the warning is replaced by a plain note. */
  confirmed: boolean;
}>;

const INTRO_CLAMP_CHARS = 180;

export function ReviewStep({ values, marketing, confirmed }: Props) {
  const { t } = useTranslation("business");
  const [introOpen, setIntroOpen] = useState(false);
  const intro = values.profileInfo;
  // A long introduction is cut at three lines until the reader asks for all of it.
  const introLong = intro.length > INTRO_CLAMP_CHARS || intro.split("\n").length > 3;

  const rows: { key: string; label: string; value: string }[] = [
    { key: "name", label: t("form.bizName"), value: values.profileName },
    { key: "latin", label: t("form.bizNameLatin"), value: values.profileNameEng },
    {
      key: "banners",
      label: t("review.banners"),
      value: marketing.banners.length > 0 ? t("review.images", { count: marketing.banners.length }) : "",
    },
    { key: "logo", label: t("marketing.logo"), value: marketing.logo ? t("review.logoSet") : "" },
  ];

  return (
    <Box className={classes.stack}>
      {confirmed ? (
        <Box className={classes.infoNote} role="note">
          <InfoIcon className={classes.infoNoteIcon} size={18} aria-hidden="true" />
          <Text component="span">{t("review.confirmedNote")}</Text>
        </Box>
      ) : (
        <Box className={classes.warnNote} role="note">
          <WarningIcon className={classes.warnIcon} size={18} aria-hidden="true" />
          <Text component="span">{t("review.warning")}</Text>
        </Box>
      )}
      <Box>
        {rows.slice(0, 2).map((row) => (
          <Box key={row.key} className={classes.reviewRow}>
            <Text component="span" className={classes.reviewLabel}>
              {row.label}
            </Text>
            <Text component="span" className={`${classes.reviewValue} ${row.value ? "" : classes.reviewValueEmpty}`}>
              {row.value || "-"}
            </Text>
          </Box>
        ))}
        <Box className={classes.reviewRow}>
          <Text component="span" className={classes.reviewLabel}>
            {t("form.bizInfo")}
          </Text>
          <Box className={classes.reviewIntroBox}>
            {intro ? (
              <>
                <Box
                  className={`${classes.reviewValue} ${classes.reviewIntro} ${introLong && !introOpen ? classes.reviewIntroClamped : ""}`}
                >
                  {intro}
                </Box>
                {introLong ? (
                  <Button variant="subtle" size="compact-sm" mt={4} onClick={() => setIntroOpen((open) => !open)} aria-expanded={introOpen}>
                    {introOpen ? t("review.showLess") : t("review.showAll")}
                  </Button>
                ) : null}
              </>
            ) : (
              <Text component="span" className={`${classes.reviewValue} ${classes.reviewValueEmpty}`}>
                -
              </Text>
            )}
          </Box>
        </Box>
        {rows.slice(2).map((row) => (
          <Box key={row.key} className={classes.reviewRow}>
            <Text component="span" className={classes.reviewLabel}>
              {row.label}
            </Text>
            <Text component="span" className={`${classes.reviewValue} ${row.value ? "" : classes.reviewValueEmpty}`}>
              {row.value || "-"}
            </Text>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
